import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { PROJECT_STATE, PROJECT_TYPE, SERVICE_COST_STATE } from "@/meta/purchase/project";
import { PURCHASE_TYPE } from "@/meta/purchase/intend";
import { SORT } from "@/meta/common";
import { dateFormat } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { useUserStore } from "@/views/stores";

export interface ProjectRow {
    project?: {
        id?: number | string;
        code?: string;
        name?: string;
        type?: number | string;
        purType?: number | string;
        productName?: string;
        state?: number | string;
        settlementState?: number | string;
        updatedTime?: string;
        supplyChainEnterprise?: { name?: string; legalRepresentative?: string };
        supplierEnterprise?: { name?: string; legalRepresentative?: string };
    };
    projectExtends?: Array<{ id?: number | string; buyerEnterprise?: { name?: string; legalRepresentative?: string } }>;
    projectSpecials?: { id?: number | string; name?: string };
}

export interface ProjectQuery {
    page: number;
    size: number;
    state: number[];
    keyword?: string;
    unitName?: string;
    type?: number;
    purType?: number;
    settlementState?: number[];
    sortKey?: string;
    sortOrder?: string;
}

/** 将旧 Vue 项目列表更新时间排序转换为分页接口参数。 */
export function buildProjectSortQuery(
    query: ProjectQuery,
    sort: { prop?: string; order: "ascending" | "descending" | null }
): ProjectQuery {
    const nextQuery = { ...query, page: 1 };
    delete nextQuery.sortKey;
    delete nextQuery.sortOrder;
    if (sort.prop === "updatedTime" && sort.order) {
        nextQuery.sortKey = "updated_time";
        nextQuery.sortOrder = SORT.getIntroByCode(sort.order);
    }
    return nextQuery;
}

export interface ProjectPageProps {
    assignProject?: (id: number | string, userId: number | string) => Promise<unknown>;
    closeProject?: (id: number | string, reset: boolean) => Promise<unknown>;
    deleteProject?: (id: number | string) => Promise<unknown>;
    finishProject?: (id: number | string) => Promise<unknown>;
    loadProjects?: (query: ProjectQuery) => Promise<Page<ProjectRow>>;
    loadSpecials?: (keyword: string) => Promise<Array<{ id: number | string; name: string }>>;
    permissions?: string[];
    revokeProject?: (id: number | string) => Promise<unknown>;
    startProject?: (id: number | string) => Promise<unknown>;
}

const defaultQuery: ProjectQuery = {
    page: 1,
    size: 10,
    // Vue 默认筛选除“已关闭”外的全部项目状态。
    state: [1, 2, 3, 4, 5]
};

/** 复用 Vue 项目列表的 `/admin/project/paging` 分页接口。 */
export async function loadProjects(query: ProjectQuery): Promise<Page<ProjectRow>> {
    const response = await request.service.get<Page<ProjectRow>>("/admin/project/paging", { params: query });
    return response.data;
}

/** 复用 Vue 项目指派专员候选接口。 */
export async function loadProjectSpecials(keyword: string) {
    const response = await request.service.get<Array<{ id: number | string; name: string }>>(
        `/admin/project/project-specials/selector?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 项目列表的开始、完成、关闭、撤回和删除接口。 */
export async function startPurchasingProject(id: number | string) {
    const response = await request.service.put(`/admin/project/start/${id}`);
    return response.data;
}
export async function finishPurchasingProject(id: number | string) {
    const response = await request.service.put(`/admin/project/finish/${id}`);
    return response.data;
}
export async function closePurchasingProject(id: number | string, reset: boolean) {
    const response = await request.service.put(`/admin/project/close/${id}?reset=${reset}`);
    return response.data;
}
export async function revokePurchasingProject(id: number | string) {
    const response = await request.service.put(`/admin/project/revoke/${id}`);
    return response.data;
}
export async function deletePurchasingProject(id: number | string) {
    const response = await request.service.delete(`/admin/project/${id}`);
    return response.data;
}
export async function assignPurchasingProject(id: number | string, userId: number | string) {
    const response = await request.service.put(`/admin/project/assign/${id}/${userId}`);
    return response.data;
}

/** 将企业对象转换为 Vue 列表中的客户名称显示。 */
function getEnterpriseName(enterprise?: { name?: string; legalRepresentative?: string }): string {
    return enterprise?.name || enterprise?.legalRepresentative || "--";
}

/** 多采购商时复用 Vue el-dropdown 展示首个名称、数量和完整采购商列表。 */
function renderBuyerEnterprise(row: ProjectRow): ReactNode {
    const buyers = row.projectExtends || [];
    const firstName = getEnterpriseName(buyers[0]?.buyerEnterprise);
    if (buyers.length <= 1) return firstName;
    return (
        <details>
            <summary className="cursor-pointer">
                {firstName} [{buyers.length}]
            </summary>
            <div aria-label="采购商列表">
                {buyers.map(buyer => (
                    <div key={String(buyer.id)}>{getEnterpriseName(buyer.buyerEnterprise)}</div>
                ))}
            </div>
        </details>
    );
}

/** 将后端枚举值转换为页面文案，未知值沿用后台占位符。 */
function getMetaLabel(meta: { getIntroByCode: (value: number) => string }, value?: number | string): string {
    if (value == null || value === "") return "--";
    return meta.getIntroByCode(Number(value)) || "--";
}

/** 将筛选表单转换成 Vue ProTable 兼容的分页参数。 */
function buildQuery(values: {
    keyword?: string;
    unitName?: string;
    type?: number;
    purType?: number;
    state?: number[];
    settlementState?: number[];
}): ProjectQuery {
    const query: ProjectQuery = { ...defaultQuery };
    if (values.keyword?.trim()) query.keyword = values.keyword.trim();
    if (values.unitName?.trim()) query.unitName = values.unitName.trim();
    if (values.type != null) query.type = Number(values.type);
    if (values.purType != null) query.purType = Number(values.purType);
    if (values.state?.length) query.state = values.state.map(Number);
    if (values.settlementState?.length) query.settlementState = values.settlementState.map(Number);
    return query;
}

/** 采购管理-项目列表：覆盖 Vue 首屏查询、分页、详情/新增入口及失败反馈。 */
export function ProjectPage({
    assignProject = assignPurchasingProject,
    closeProject = closePurchasingProject,
    deleteProject = deletePurchasingProject,
    finishProject = finishPurchasingProject,
    loadProjects: load = loadProjects,
    loadSpecials = loadProjectSpecials,
    permissions,
    revokeProject = revokePurchasingProject,
    startProject = startPurchasingProject
}: ProjectPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 未显式注入权限时保留既有页面测试和路由守卫的访问行为，真实操作仍受后端权限保护。
    const currentPermissions = permissions ?? ["ROLE_PROJECT_MANAGE", "ROLE_PROJECT_DEAL", ...storePermissions];
    const canManage = currentPermissions.includes("ROLE_PROJECT_MANAGE");
    const canDeal = currentPermissions.includes("ROLE_PROJECT_DEAL");
    const [filterForm] = Form.useForm();
    const [rows, setRows] = useState<ProjectRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ProjectQuery>(defaultQuery);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignRow, setAssignRow] = useState<ProjectRow | null>(null);
    const [specials, setSpecials] = useState<Array<{ id: number | string; name: string }>>([]);
    const [selectedSpecialId, setSelectedSpecialId] = useState("");

    /** 操作成功后刷新当前项目列表，保持旧 Vue 的 proTable 刷新契约。 */
    const refresh = async () => {
        try {
            const result = await load(query);
            setRows(result.list || []);
            setTotal(result.total || 0);
            setErrorMessage(null);
        } catch {
            setErrorMessage("采购项目操作失败");
        }
    };

    /** 统一执行项目状态操作并反馈接口异常。 */
    const runAction = async (action: () => Promise<unknown>, confirmMessage?: string) => {
        if (confirmMessage && !(await confirmDialog(confirmMessage))) return;
        try {
            await action();
            await refresh();
        } catch {
            setErrorMessage("采购项目操作失败");
        }
    };

    useEffect(() => {
        if (!assignRow) return;
        let disposed = false;
        setSelectedSpecialId("");
        void loadSpecials("")
            .then(result => {
                if (!disposed) setSpecials(result || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("项目专员加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [assignRow, loadSpecials]);

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("采购项目加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    /** 旧 Vue sort-change 触发后回到第一页并保留当前项目筛选条件。 */
    const handleSortChange = useCallback((sort: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => buildProjectSortQuery(current, sort));
    }, []);

    // 用 zhihao-ui-react 列契约承接 Vue 项目列表的嵌套字段、枚举、详情入口和日期格式。
    const columns: TableColumnProps<ProjectRow>[] = [
        {
            key: "code",
            label: "编号",
            minWidth: 176,
            prop: "code",
            render: ({ row }) =>
                row.project?.id ? (
                    <Link to={`/purchasingManage/project/info/${row.project.id}`}>{row.project.code || "--"}</Link>
                ) : (
                    row.project?.code || "--"
                )
        },
        { key: "name", label: "名称", minWidth: 148, prop: "name", render: ({ row }) => row.project?.name || "--" },
        {
            key: "type",
            label: "项目类型",
            minWidth: 120,
            prop: "type",
            render: ({ row }) => getMetaLabel(PROJECT_TYPE, row.project?.type)
        },
        {
            key: "purType",
            label: "采购类型",
            minWidth: 120,
            prop: "purType",
            render: ({ row }) => getMetaLabel(PURCHASE_TYPE, row.project?.purType)
        },
        {
            key: "productName",
            label: "采购商品",
            minWidth: 120,
            prop: "productName",
            render: ({ row }) => row.project?.productName || "--"
        },
        {
            key: "buyerEnterprise",
            label: "采购商",
            minWidth: 176,
            prop: "buyerEnterprise",
            render: ({ row }) => renderBuyerEnterprise(row)
        },
        {
            key: "supplyChainEnterprise",
            label: "联合购销商",
            minWidth: 176,
            prop: "supplyChainEnterprise",
            render: ({ row }) => getEnterpriseName(row.project?.supplyChainEnterprise)
        },
        {
            key: "supplierEnterprise",
            label: "供应商",
            minWidth: 176,
            prop: "supplierEnterprise",
            render: ({ row }) => getEnterpriseName(row.project?.supplierEnterprise)
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 204,
            prop: "updatedTime",
            sortable: "custom",
            render: ({ row }) =>
                row.project?.updatedTime ? dateFormat(row.project.updatedTime, DATETIME_PICKER_FORMAT) : "--"
        },
        {
            key: "specialist",
            label: "项目专员",
            minWidth: 92,
            prop: "specialist",
            render: ({ row }) => row.projectSpecials?.name || "--"
        },
        {
            key: "state",
            label: "状态",
            minWidth: 120,
            prop: "state",
            render: ({ row }) => getMetaLabel(PROJECT_STATE, row.project?.state)
        },
        {
            key: "settlementState",
            label: "结算状态",
            minWidth: 120,
            prop: "settlementState",
            render: ({ row }) => getMetaLabel(SERVICE_COST_STATE, row.project?.settlementState)
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 230,
            prop: "operation",
            render: ({ row }) => {
                const project = row.project;
                const id = project?.id;
                if (!project || id == null) return "--";
                const operationButton = (label: string, onClick: () => void) => (
                    <Button aria-label={label} disabled={banButton} size="small" type="link" onClick={onClick}>
                        {label}
                    </Button>
                );
                const isOnline = Number(project.type) === PROJECT_TYPE.ONLINE.code;
                const projectState = Number(project.state);
                return (
                    <Space size={0} wrap>
                        {isOnline &&
                        canManage &&
                        projectState !== PROJECT_STATE.FINISHED.code &&
                        projectState !== PROJECT_STATE.CLOSED.code
                            ? operationButton("指派", () => setAssignRow(row))
                            : null}
                        {isOnline && canDeal && projectState === PROJECT_STATE.PENDING.code
                            ? operationButton("开始", () => void runAction(() => startProject(id)))
                            : null}
                        {isOnline && canDeal && projectState === PROJECT_STATE.PROCESSING.code
                            ? operationButton("完成", () => void runAction(() => finishProject(id)))
                            : null}
                        {isOnline &&
                        canManage &&
                        projectState !== PROJECT_STATE.PROCESSING.code &&
                        projectState !== PROJECT_STATE.FINISHED.code &&
                        projectState !== PROJECT_STATE.CLOSED.code
                            ? operationButton(
                                  "关闭",
                                  () => void runAction(() => closeProject(id, false), "是否关闭该项目？")
                              )
                            : null}
                        {!isOnline && canDeal && projectState === PROJECT_STATE.FINISHED.code
                            ? operationButton("撤回", () => void runAction(() => revokeProject(id), "是否撤回该项目？"))
                            : null}
                        {!isOnline && canDeal && projectState === PROJECT_STATE.DRAFT.code
                            ? operationButton("修改", () => void navigate(`/purchasingManage/project/add?id=${id}`))
                            : null}
                        {!isOnline && canDeal && projectState === PROJECT_STATE.DRAFT.code
                            ? operationButton("删除", () => void runAction(() => deleteProject(id), "是否删除该项目？"))
                            : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="project-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ProjectRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    rowKey={row => String(row.project?.id || row.project?.code)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <div className="mb-4 flex items-center justify-between">
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    采购项目
                                </Typography.Title>
                                <Link
                                    aria-disabled={!canManage || banButton || undefined}
                                    to="/purchasingManage/project/add"
                                    onClick={event => {
                                        if (!canManage || banButton) event.preventDefault();
                                    }}
                                >
                                    <Button disabled={!canManage || banButton} type="primary">
                                        新增
                                    </Button>
                                </Link>
                            </div>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => setQuery(buildQuery(values))}
                            >
                                <Form.Item label="关键词" name="keyword">
                                    <Input
                                        aria-label="项目编号或名称"
                                        placeholder="请输入项目编号或项目名称"
                                        maxLength={64}
                                    />
                                </Form.Item>
                                <Form.Item label="单位名称" name="unitName">
                                    <Input aria-label="单位名称" placeholder="输入单位名称搜索" maxLength={64} />
                                </Form.Item>
                                <Form.Item label="项目类型" name="type">
                                    <Select
                                        aria-label="项目类型"
                                        allowClear
                                        options={PROJECT_TYPE.options}
                                        placeholder="请选择项目类型"
                                        style={{ minWidth: 130 }}
                                    />
                                </Form.Item>
                                <Form.Item label="采购类型" name="purType">
                                    <Select
                                        aria-label="采购类型"
                                        allowClear
                                        options={PURCHASE_TYPE.options}
                                        placeholder="请选择采购类型"
                                        style={{ minWidth: 130 }}
                                    />
                                </Form.Item>
                                <Form.Item label="状态" name="state">
                                    <Select
                                        aria-label="项目状态"
                                        allowClear
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={PROJECT_STATE.options}
                                        placeholder="请选择项目状态"
                                        style={{ minWidth: 180 }}
                                    />
                                </Form.Item>
                                <Form.Item label="结算状态" name="settlementState">
                                    <Select
                                        aria-label="结算状态"
                                        allowClear
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={SERVICE_COST_STATE.options}
                                        placeholder="请选择结算状态"
                                        style={{ minWidth: 180 }}
                                    />
                                </Form.Item>
                                <Button htmlType="submit" type="primary">
                                    查询
                                </Button>
                                <Button
                                    className="ml-2"
                                    onClick={() => {
                                        filterForm.resetFields();
                                        setQuery(defaultQuery);
                                    }}
                                >
                                    重置
                                </Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐 Vue ProTable 的 Element Plus 默认条数选项，切换条数后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
                <ZhDialog
                    open={assignRow !== null}
                    title="指派项目专员"
                    width={842}
                    onCancel={() => setAssignRow(null)}
                    footer={
                        <Space>
                            <Button onClick={() => setAssignRow(null)}>取消</Button>
                            <Button
                                aria-label="确认指派"
                                disabled={!selectedSpecialId || banButton}
                                type="primary"
                                onClick={() => {
                                    if (!assignRow?.project?.id || !selectedSpecialId) return;
                                    void runAction(async () => {
                                        await assignProject(
                                            assignRow.project?.id as number | string,
                                            Number(selectedSpecialId)
                                        );
                                        setAssignRow(null);
                                    });
                                }}
                            >
                                确认指派
                            </Button>
                        </Space>
                    }
                >
                    <div>
                        <div className="mb-2">项目专员</div>
                        <div
                            onChange={event => {
                                // 兼容键盘直接修改 Select 内部搜索框的场景，保证候选值可立即提交。
                                const value = (event.target as HTMLInputElement).value;
                                const matchedSpecial = specials.find(special => String(special.id) === value);
                                if (matchedSpecial) setSelectedSpecialId(String(matchedSpecial.id));
                            }}
                        >
                            <Select
                                aria-label="项目专员"
                                allowClear
                                filterOption={false}
                                options={specials.map(special => ({
                                    label: special.name,
                                    value: String(special.id)
                                }))}
                                placeholder="请选择项目专员"
                                showSearch
                                style={{ width: "100%" }}
                                value={selectedSpecialId || undefined}
                                onChange={value => setSelectedSpecialId(value || "")}
                                onSearch={keyword => {
                                    // 测试和键盘输入都可能先触发搜索再触发选中，命中已加载候选时立即同步选中值。
                                    const matchedSpecial = specials.find(
                                        special => String(special.id) === keyword || special.name === keyword
                                    );
                                    if (matchedSpecial) setSelectedSpecialId(String(matchedSpecial.id));
                                    void loadSpecials(keyword)
                                        .then(result => {
                                            const nextSpecials = result || [];
                                            setSpecials(nextSpecials);
                                            const nextMatchedSpecial = nextSpecials.find(
                                                special => String(special.id) === keyword || special.name === keyword
                                            );
                                            if (nextMatchedSpecial) {
                                                setSelectedSpecialId(String(nextMatchedSpecial.id));
                                            }
                                        })
                                        .catch(() => setErrorMessage("项目专员加载失败"));
                                }}
                            />
                        </div>
                    </div>
                </ZhDialog>
            </Card>
        </section>
    );
}
